import React, { useState } from 'react';
import { Loader2, Zap, Settings, Database, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { nodeTypeConfig } from '@/components/workflow/WorkflowNodeTypes';
import { nodeIconMap } from '@/components/workflow/NodePalette';
import { categoryLabels } from '@/stores/workflow/node-registry-store';
import { ScreenListPanel } from '@/components/screens';
import { SchemaListPanel } from '@/components/schemas';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';

/**
 * Structural left sidebar for workflow editor (embedded mode).
 * Replaces FloatingNodePalette with a proper layout-level sidebar.
 */
export const WorkflowNodePaletteSidebar: React.FC = () => {
  const {
    nodeRegistryLoading,
    getFilteredNodesByCategory,
    leftSidebarTab,
    setLeftSidebarTab,
  } = useWorkflowEditor();

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex flex-col h-full">
      {/* Tabs + collapse toggle */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-border">
        {(['nodes', 'screens', 'schemas'] as const).map(tab => (
          <button
            key={tab}
            className={cn(
              'px-2 py-1.5 text-xs font-medium rounded transition-colors',
              leftSidebarTab === tab
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            onClick={() => {
              setLeftSidebarTab(tab);
              if (collapsed) setCollapsed(false);
            }}
          >
            {tab === 'nodes' && <Zap className="w-3 h-3 inline mr-1" />}
            {tab === 'screens' && <Settings className="w-3 h-3 inline mr-1" />}
            {tab === 'schemas' && <Database className="w-3 h-3 inline mr-1" />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Content */}
      {!collapsed && <ScrollArea className="flex-1 min-h-0">
        {leftSidebarTab === 'nodes' && (
          <div className="p-3 space-y-3">
            {nodeRegistryLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              getFilteredNodesByCategory().map((categoryGroup) => (
                <div key={categoryGroup.category}>
                  <h4 className="text-xs font-medium text-muted-foreground uppercase mb-1.5">
                    {categoryLabels[categoryGroup.category] || categoryGroup.category}
                  </h4>
                  <div className="space-y-1">
                    {categoryGroup.nodes.map((node) => {
                      const localConfig = nodeTypeConfig[node.type as keyof typeof nodeTypeConfig];
                      const Icon = localConfig?.icon;
                      const bgColor = node.color || localConfig?.color || 'bg-gray-500';

                      return (
                        <div
                          key={node.type}
                          draggable
                          onDragStart={(event) => {
                            event.dataTransfer.setData('application/reactflow', node.type);
                            event.dataTransfer.effectAllowed = 'move';
                          }}
                          className="flex items-center gap-2 p-2 rounded border bg-card hover:bg-accent hover:border-blue-500 cursor-move transition-colors"
                        >
                          <div
                            className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: bgColor.startsWith('#') ? bgColor : undefined }}
                          >
                            {(() => {
                              if (Icon) return <Icon className="w-3 h-3 text-white" />;
                              const MappedIcon = nodeIconMap[node.icon] || Circle;
                              return <MappedIcon className="w-3 h-3 text-white" />;
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs">{node.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {node.description}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {leftSidebarTab === 'screens' && <ScreenListPanel className="flex-1" />}
        {leftSidebarTab === 'schemas' && <SchemaListPanel className="flex-1" />}
      </ScrollArea>}
    </div>
  );
};
